var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import { jsx as _jsx, jsxs as _jsxs } from "theme-ui/jsx-runtime";
import { FocusTrap } from "focus-trap-react";
import { AnimatePresence, motion } from "framer-motion";
import { useEffect } from "react";
import { Box, Flex } from "theme-ui";
import { Column, HeadingRegularVariants, ifBrowser, modalMotion, overLayMotion, zIndices } from "../../index";
import { turnOffDocumentBodyScroll, turnOnDocumentBodyScroll } from "../../utils/document";
import ClientOnlyPortal from "./ClientOnlyPortal";
const CloseButton = ({ onClick, mode = "light", style = {} }) => (_jsx("button", { "aria-label": "Close Modal", onClick: onClick, sx: Object.assign({ variant: "buttons.hollowLight", backgroundColor: mode === "light" ? "transparent" : "#262626", "&:hover": {
            backgroundColor: mode === "light" ? "inherit" : "#262626"
        } }, style), children: _jsxs("svg", { width: "12", height: "11", viewBox: "0 0 12 11", fill: "none", xmlns: "http://www.w3.org/2000/svg", children: [_jsx("path", { d: "M12 10.2857L7.33973 5.14286L12 0L9.80313 0L5.14286 5.14286L9.80313 10.2857H12Z", fill: "white" }), _jsx("path", { d: "M1.32911e-06 -9.53674e-07L4.66027 5.14286L0 10.2857H2.19687L6.85714 5.14286L2.19687 -9.53674e-07H1.32911e-06Z", fill: "white" })] }) }));
export const Modal = (_a) => {
    var { show, close } = _a, props = __rest(_a, ["show", "close"]);
    // the reason why we do this here is because turnOnDocumentBodyScroll removes the fixed position of the document
    // and also scrolls window to the Y position before the modal was opened.
    // This scroll is causing some blinking on the page if done when component dismounts on useEffect, which is why we do it just before that happens.
    const handleModalClose = () => {
        turnOnDocumentBodyScroll();
        close();
    };
    useEffect(() => {
        const escapeKeyListener = (ev) => {
            if (show && ev.key === "Escape") {
                close();
            }
        };
        ifBrowser(() => {
            if (typeof window !== "undefined") {
                window.document.addEventListener("keydown", escapeKeyListener);
            }
        });
        return () => {
            ifBrowser(() => {
                if (typeof window !== "undefined") {
                    window.document.removeEventListener("keydown", escapeKeyListener);
                }
            });
        };
    }, [show]);
    return (_jsx(ClientOnlyPortal, { children: _jsx(AnimatePresence, { children: show && (_jsx(ModalComponent, Object.assign({ show: show, close: handleModalClose }, props))) }) }));
};
const ModalComponent = ({ show, close, title = "", backgroundColor = "", children, maxHeight = ["calc(100vh - 16rem)"], width = [504], innerHeight = ["auto"], height = [null, "auto"], paddingX = 24, paddingY = 24 }) => {
    useEffect(() => {
        if (show) {
            turnOffDocumentBodyScroll();
        }
        return () => {
            // The assumption is that removing the modal from the tree means the
            // modal is not shown anymore. Therefore we can reset the styling.
            turnOnDocumentBodyScroll();
        };
    }, []);
    return (_jsx(FocusTrap, { focusTrapOptions: { clickOutsideDeactivates: true }, children: _jsx("div", { role: "dialog", "aria-labelledby": "modalTitle", sx: {
                zIndex: zIndices.overlay,
                position: "fixed",
                top: 0,
                left: 0,
                width: "100%",
                height: "100%"
            }, children: _jsx(motion.div, Object.assign({}, overLayMotion, { children: _jsx(motion.div, Object.assign({ sx: {
                        width: "100%",
                        height: "100%"
                    } }, modalMotion, { children: _jsx(Flex, { sx: {
                            justifyContent: "center",
                            height: "100%"
                        }, children: _jsxs(Column, { sx: {
                                position: ["fixed", "relative"],
                                top: 0,
                                width: ["100%", "100%", ...width],
                                height: ["100%", ...height],
                                my: "auto"
                            }, children: [title && (_jsxs(Flex, { id: "modalTitle", sx: {
                                        flexDirection: "row",
                                        bg: "blue.2",
                                        alignItems: "center",
                                        pl: 24,
                                        height: 64
                                    }, children: [_jsx(Box, { sx: { flexGrow: 1, color: "white.0" }, children: _jsx("h3", { sx: { variant: `text.${HeadingRegularVariants.sixth}` }, children: title }) }), _jsx(Box, { children: _jsx(CloseButton, { onClick: close, mode: "light" }) })] })), !title && (_jsx(Box, { id: "modalTitle", sx: {
                                        position: "absolute",
                                        top: [16, 16, "40px"],
                                        right: [16, 16, "80px"]
                                    }, children: _jsx(CloseButton, { style: {
                                            height: [40, 40, 24],
                                            width: [40, 40, 24],
                                            zIndex: 10,
                                            padding: 8,
                                            borderRadius: 0
                                        }, onClick: close, mode: "dark" }) })), _jsx(Flex, { sx: {
                                        px: paddingX,
                                        py: paddingY,
                                        bg: backgroundColor || "white.0",
                                        overflow: ["scroll", "auto"],
                                        height: ["100%", ...innerHeight],
                                        maxHeight: ["100%", ...maxHeight],
                                        flexDirection: "column"
                                    }, children: children })] }) }) }), `modal-content`) }), `modal-bg`) }) }));
};
//# sourceMappingURL=Modal.js.map